import React, { useState } from "react";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import useReveal from "@/hooks/useReveal";

export default function Contact() {
  useReveal();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const areasOfInterest = t("contact.areasOfInterest", { returnObjects: true });

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  }

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow={t("contact.heroEyebrow")}
        title={t("contact.heroTitle")}
        accent={t("contact.heroAccent")}
        sub={t("contact.heroSub")}
        bgImage="/assets/9eitpmqk_hanoi-building.jpg"
      />

      {/* QUICK CONTACT DETAILS */}
      <section className="relative bg-white py-20 lg:py-24 border-b border-[#14355a]/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid md:grid-cols-3 gap-10">
          {[
            { Icon: Mail, label: t("contact.generalInquiries"), value: "info@unikholdings.com" },
            { Icon: Phone, label: t("contact.phone"), value: "(+84) 2422001076" },
            { Icon: MapPin, label: t("contact.address"), value: "4 Liễu Giai, Ngọc Hà, Hà Nội" },
          ].map((c, i) => (
            <div
              key={c.label}
              data-reveal
              style={{ "--reveal-delay": `${i * 120}ms` }}
              className="flex items-start gap-5"
            >
              <c.Icon size={26} className="text-unik-gold flex-shrink-0 mt-1" strokeWidth={1.4} />
              <div>
                <div className="eyebrow text-[#14355a]">{c.label}</div>
                <div className="mt-3 font-display text-[#14355a] text-base lg:text-lg" style={{ fontWeight: 500 }}>
                  {c.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SINGLE UNIFIED FORM */}
      <section className="relative bg-unik-cream py-28 lg:py-36">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div data-reveal className="mb-12 lg:mb-16 text-center">
            <div className="eyebrow text-[#14355a]">{t("contact.howHelp")}</div>
            <div className="mt-6 w-12 h-px bg-unik-gold mx-auto" />
            <h2
              style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)", fontWeight: 500, lineHeight: "1.0" }}
              className="mt-8 font-display text-[#14355a] tracking-tight"
            >
              {t("contact.getInTouch")}
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-[16px] lg:text-[17px] leading-[1.85] text-unik-ink/75">
              {t("contact.getInTouchBody")}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="form-unified"
            data-reveal
            className="bg-white p-8 lg:p-12 border border-[#14355a]/10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <FormField label={t("contact.form.fullName")} name="name" required />
              <FormField label={t("contact.form.email")} name="email" type="email" required />
              <FormField label={t("contact.form.company")} name="company" />
              <FormField label={t("contact.form.phone")} name="phone" />
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-unik-ink/55 font-medium mb-3">
                  {t("contact.form.areaOfInterest")}
                </label>
                <select
                  name="subject"
                  data-testid="subject-select"
                  className="w-full bg-transparent border-b border-[#14355a]/25 py-3 text-[15px] text-[#14355a] focus:border-unik-gold outline-none transition-colors"
                >
                  {areasOfInterest.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-unik-ink/55 font-medium mb-3">
                  {t("contact.form.message")}
                </label>
                <textarea
                  name="message"
                  rows={5}
                  required
                  className="w-full bg-transparent border-b border-[#14355a]/25 py-3 text-[15px] text-[#14355a] focus:border-unik-gold outline-none transition-colors resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="form-unified-submit"
              className="mt-12 group inline-flex items-center gap-3 px-10 py-4 rounded-full bg-[#14355a] text-white font-display text-sm tracking-wide hover:bg-unik-gold hover:text-[#14355a] transition-all duration-500"
            >
              {submitted ? t("contact.form.thanks") : t("contact.form.submit")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </section>

      {/* MAP */}
      <section className="relative bg-unik-cream pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal-fade className="overflow-hidden border border-[#14355a]/10">
            <iframe
              title="UNIK Holdings — Hanoi"
              src="https://www.google.com/maps?q=C%C3%B4ng+ty+C%E1%BB%95+ph%E1%BA%A7n+Unik+Holdings&ll=21.0368918,105.8144216&z=17&output=embed"
              className="w-full h-[420px] lg:h-[520px] block"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function FormField({ label, name, type = "text", required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-unik-ink/55 font-medium mb-3">
        {label}
        {required && <span className="text-unik-gold ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        className="w-full bg-transparent border-b border-[#14355a]/25 py-3 text-[15px] text-[#14355a] focus:border-unik-gold outline-none transition-colors"
      />
    </div>
  );
}
